import { createReducer } from '@reduxjs/toolkit';
import {
  constants as appStorageConstants,
  records as appStorageRecords,
} from '@saucelabs/sl-module-app-storage';

import actions from './actions';
import operations from './operations';
import { operations as settingsOperations } from '../settings';
import constants from './constants';
import globalConstants from '../../constants';
import { FilterOsValue } from './utils';
import { ValueOf } from '../../GlobalTypes';

const { FILTER_OS_VALUE_ALL, FETCH_GROUPS_URL_PARAMS } = constants;

interface GroupsState {
  readonly currentPage: number
  readonly filterOs: FilterOsValue
  // Note: using an array for currentGroup, because typing it directly
  // as class instance leads to the store transforming it into a
  // plain object in some cases, which may lead to crashes and missing functionality
  readonly currentGroup: (appStorageRecords.AppStorageGroup | undefined)[]
  readonly hasError: boolean
  readonly isLoading: boolean
  readonly items: (appStorageRecords.AppStorageGroup | undefined)[]
  readonly itemsPerPage: number
  readonly searchQuery: string
  readonly totalItems: number
}

const initialState: GroupsState = {
  currentPage: 1,
  filterOs: FILTER_OS_VALUE_ALL,
  currentGroup: [],
  hasError: false,
  isLoading: false,
  items: [],
  itemsPerPage: globalConstants.TABLE_PAGE_SIZES[0],
  searchQuery: '',
  totalItems: 0,
};

type AppStorageKindValues = ValueOf<typeof appStorageConstants.APP_STORAGE_KIND>

const deriveFilterOsValue = (url: string | undefined): FilterOsValue => {
  const urlSearchParams = new URLSearchParams(url);
  const kindValues = urlSearchParams.getAll(FETCH_GROUPS_URL_PARAMS.KIND);
  const sanitizedKindValues = [...new Set(kindValues
    .filter((kind: AppStorageKindValues) => (
      Object.values(appStorageConstants.APP_STORAGE_KIND).includes(kind)
    )))] as AppStorageKindValues[];

  if (sanitizedKindValues.length === 1) {
    return sanitizedKindValues[0];
  }
  return FILTER_OS_VALUE_ALL;
};

const deriveSearchQueryValue = (url: string | undefined): string => {
  const urlSearchParams = new URLSearchParams(url);
  const value = urlSearchParams.get(FETCH_GROUPS_URL_PARAMS.SEARCH_QUERY);
  return value ?? '';
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const deriveUpdatedSettingsValue = (
  kind: AppStorageKindValues,
  originalSettings: appStorageRecords.AppStorageGroupSettings = {},
  updatedSettings: appStorageRecords.AppStorageGroupSettings = {},
) => {
  if (kind === appStorageConstants.APP_STORAGE_KIND.ANDROID) {
    return new appStorageRecords.AppStorageGroupSettingsAndroid({
      ...originalSettings,
      ...updatedSettings,
    });
  }
  if (kind === appStorageConstants.APP_STORAGE_KIND.IOS) {
    return new appStorageRecords.AppStorageGroupSettingsIos({
      ...originalSettings,
      ...updatedSettings,
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return {
    ...originalSettings,
    ...updatedSettings,
  };
};

const groupsReducer = createReducer(initialState, (builder) => {
  builder.addCase(actions.fetchGroups.pending, (draft) => {
    draft.isLoading = true;
  });

  builder.addCase(actions.fetchGroups.fulfilled, (draft, { payload }) => {
    draft.currentPage = payload.page;
    draft.filterOs = deriveFilterOsValue(payload.links.self);
    draft.hasError = false;
    draft.isLoading = false;
    draft.items = payload.items;
    draft.itemsPerPage = payload.per_page;
    draft.searchQuery = deriveSearchQueryValue(payload.links.self);
    draft.totalItems = payload.total_items;
  });

  builder.addCase(actions.fetchGroups.rejected, (draft) => {
    draft.hasError = true;
    draft.isLoading = false;
  });

  builder.addCase(actions.setCurrentGroup, (draft, { payload }) => {
    draft.currentGroup = [payload];
  });

  builder.addCase(operations.deleteAndRefreshGroups.pending, (draft) => {
    draft.isLoading = true;
  });

  builder.addCase(operations.deleteAndRefreshGroups.fulfilled, (draft) => {
    draft.isLoading = false;
  });

  builder.addCase(operations.deleteAndRefreshGroups.rejected, (draft) => {
    draft.isLoading = false;
  });

  builder.addCase(actions.fetchGroup.pending, (draft, { meta }) => {
    const groupId = parseInt(meta.arg, 10);
    const affectedGroupIndex = draft.items.findIndex((value) => value?.id === groupId);
    if (affectedGroupIndex >= 0) {
      draft.currentGroup = [draft.items[affectedGroupIndex]];
    } else {
      draft.currentGroup = [];
    }
  });

  builder.addCase(actions.fetchGroup.fulfilled, (draft, { payload }) => {
    draft.currentGroup = payload.items.slice(0, 1);
  });

  builder.addCase(actions.fetchGroup.rejected, (draft) => {
    draft.currentGroup = [];
  });

  builder.addCase(settingsOperations.updateSettings.pending, (draft, { meta }) => {
    // Update the UI immediately
    const { groupId } = meta.arg;
    const currentGroup = draft.currentGroup[0];
    if (currentGroup && currentGroup.id === groupId) {
      const { updatedValues } = meta.arg;

      currentGroup.settings = deriveUpdatedSettingsValue(
        currentGroup.recent.kind,
        currentGroup.settings ?? {},
        updatedValues,
      );
      draft.currentGroup = [currentGroup]; // assign a new array to trigger a selector update
    }
  });

  builder.addCase(settingsOperations.updateSettings.fulfilled, (draft, { meta, payload }) => {
    // Update the settings with the server response to use the most recent data
    const { groupId } = meta.arg;
    const currentGroup = draft.currentGroup[0];
    if (currentGroup && currentGroup.id === groupId) {
      currentGroup.settings = deriveUpdatedSettingsValue(
        currentGroup.recent.kind,
        currentGroup.settings ?? {},
        payload.settings,
      );
      draft.currentGroup = [currentGroup]; // assign a new array to trigger a selector update
    }
  });

  builder.addCase(settingsOperations.updateSettings.rejected, (draft, { meta }) => {
    // Roll back to previous state if the action failed
    const { groupId } = meta.arg;
    const currentGroup = draft.currentGroup[0];
    if (currentGroup && currentGroup.id === groupId) {
      const { originalValues } = meta.arg;

      currentGroup.settings = deriveUpdatedSettingsValue(
        currentGroup.recent.kind,
        currentGroup.settings ?? {},
        originalValues,
      );
      draft.currentGroup = [currentGroup]; // assign a new array to trigger a selector update
    }
  });
});

export default groupsReducer;
export {
  deriveFilterOsValue,
  deriveSearchQueryValue,
  deriveUpdatedSettingsValue,
  GroupsState,
  initialState,
};
